/**
 * Pricing API client.
 *
 * Network-layer foundation for every CON-PRICE-* ticket. One named export
 * per backend endpoint (eleven total — five product-scoped admin, five
 * material-scoped admin, one public). Each function:
 *
 *   1. Calls the configured shared axios instance with the right path,
 *      method, and body — no per-call auth headers, no per-call baseURL,
 *      no envelope assembly.
 *   2. Unwraps the success envelope into the typed `PricingTierDTO` (or
 *      `PricingTierDTO[]` for list endpoints).
 *   3. Translates any non-2xx response into an {@link ApiError} that
 *      preserves the platform envelope's `status`, `severity`, `message`,
 *      `errors` map (422), and `error_code` (e.g. 409 range overlap).
 *
 * The two admin scopes (Product and ProductMaterial) intentionally surface
 * as ten distinct functions rather than a single `pricingApi.create({ scope,
 * ... })` with a discriminator argument. The registry of "which API
 * function the current scope maps to" lives in the container layer (see
 * `.claude/rules/frontend-data-flow.md` §4) — collapsing them here would
 * pull that registry concern down into the network module.
 *
 * CON-PRICE-001.
 */
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  BillableAreaDTO,
  CreateCustomSizePricingRatePayload,
  CreatePricingTierPayload,
  CustomSizePricingRateDTO,
  PredefinedSizeMatrixRowDTO,
  PriceBreakDTO,
  PriceBreaksParams,
  PricingTierDTO,
  SavePredefinedSizeMatrixPayload,
  UpdateCustomSizePricingRatePayload,
  UpdatePricingTierPayload,
} from '@dfq/shared/types/pricing';

import { apiClient } from './client';
import { throwAsApiError, unwrap } from './_envelope';

// ----------------------------------------------------------------------------
// Path builders — kept private. They exist purely to deduplicate the URL
// construction across the parallel scopes; do NOT export them. Containers
// that need URL strings should call the matching exported function.
// ----------------------------------------------------------------------------

function productTierCollectionPath(productId: number): string {
  return `/admin/products/${encodeURIComponent(String(productId))}/pricing-tiers`;
}

function productTierItemPath(productId: number, tierId: number): string {
  return `${productTierCollectionPath(productId)}/${encodeURIComponent(String(tierId))}`;
}

function materialTierCollectionPath(productId: number, materialId: number): string {
  return (
    `/admin/products/${encodeURIComponent(String(productId))}` +
    `/materials/${encodeURIComponent(String(materialId))}/pricing-tiers`
  );
}

function materialTierItemPath(productId: number, materialId: number, tierId: number): string {
  return `${materialTierCollectionPath(productId, materialId)}/${encodeURIComponent(String(tierId))}`;
}

function publicProductTierCollectionPath(productId: number): string {
  return `/products/${encodeURIComponent(String(productId))}/pricing-tiers`;
}

// ----------------------------------------------------------------------------
// Envelope projections — pulled out of every endpoint so the data shape
// stays in one place. The platform's PricingTierTemplate emits `data.tier`
// for single-row endpoints and `data.tiers` for list endpoints.
// ----------------------------------------------------------------------------

interface SingleTierData {
  tier: PricingTierDTO;
}

interface TierListData {
  tiers: PricingTierDTO[];
}

interface PredefinedSizeMatrixData {
  predefined_sizes: PredefinedSizeMatrixRowDTO[];
}

function predefinedSizeMatrixPath(configId: number): string {
  return `/admin/product-type-configs/${encodeURIComponent(String(configId))}/predefined-sizes`;
}

async function unwrapMatrixResponse(
  promise: Promise<{ status: number; data: ApiEnvelope<PredefinedSizeMatrixData> }>,
): Promise<PredefinedSizeMatrixRowDTO[]> {
  try {
    const response = await promise;
    const data = unwrap(response.data, response.status);
    return data.predefined_sizes ?? [];
  } catch (error) {
    throwAsApiError(error);
  }
}

async function unwrapTierResponse(
  promise: Promise<{ status: number; data: ApiEnvelope<SingleTierData> }>,
): Promise<PricingTierDTO> {
  try {
    const response = await promise;
    const data = unwrap(response.data, response.status);
    if (!data.tier) {
      throw new Error('Pricing tier envelope missing `data.tier`');
    }
    return data.tier;
  } catch (error) {
    throwAsApiError(error);
  }
}

async function unwrapTiersResponse(
  promise: Promise<{ status: number; data: ApiEnvelope<TierListData> }>,
): Promise<PricingTierDTO[]> {
  try {
    const response = await promise;
    const data = unwrap(response.data, response.status);
    return data.tiers ?? [];
  } catch (error) {
    throwAsApiError(error);
  }
}

// ============================================================================
// Product-scope (admin) — 5 endpoints
// ============================================================================

/**
 * `POST /api/v1/admin/products/{productId}/pricing-tiers`
 *
 * Creates a new product-scope pricing tier. Range-overlap with an existing
 * tier returns 409 (severity: warning, message: "Pricing tier range
 * overlaps with existing tier"). Validation failures return 422 with the
 * field-keyed `errors` map. Both surface as {@link ApiError}.
 */
export async function createProductTier(
  productId: number,
  payload: CreatePricingTierPayload,
): Promise<PricingTierDTO> {
  return unwrapTierResponse(
    apiClient.post<ApiEnvelope<SingleTierData>>(productTierCollectionPath(productId), payload),
  );
}

/**
 * `GET /api/v1/admin/products/{productId}/pricing-tiers`
 *
 * Lists every product-scope tier ordered by `minQty` ascending. Empty
 * array (NOT 404) is the valid "no tiers configured" response — the
 * storefront price calculator interprets that as "use the product base
 * price." Returns 404 only when the product itself does not exist.
 */
export async function listProductTiers(productId: number): Promise<PricingTierDTO[]> {
  return unwrapTiersResponse(
    apiClient.get<ApiEnvelope<TierListData>>(productTierCollectionPath(productId)),
  );
}

/**
 * `GET /api/v1/admin/products/{productId}/pricing-tiers/{tierId}`
 *
 * Reads a single product-scope tier. Returns 404 when the tier does not
 * belong to the resolved product (the platform's priceable tuple-guard
 * prevents cross-priceable id leakage).
 */
export async function getProductTier(
  productId: number,
  tierId: number,
): Promise<PricingTierDTO> {
  return unwrapTierResponse(
    apiClient.get<ApiEnvelope<SingleTierData>>(productTierItemPath(productId, tierId)),
  );
}

/**
 * `PUT /api/v1/admin/products/{productId}/pricing-tiers/{tierId}`
 *
 * Partial update — payload must carry at least one of `min_qty`,
 * `max_qty`, or `price_per_unit` (the type system enforces this at the
 * call site). Empty body is rejected by the platform with 422.
 * Range-overlap surfaces as 409.
 */
export async function updateProductTier(
  productId: number,
  tierId: number,
  payload: UpdatePricingTierPayload,
): Promise<PricingTierDTO> {
  return unwrapTierResponse(
    apiClient.put<ApiEnvelope<SingleTierData>>(productTierItemPath(productId, tierId), payload),
  );
}

/**
 * `DELETE /api/v1/admin/products/{productId}/pricing-tiers/{tierId}`
 *
 * Hard-deletes a product-scope tier. Returns 204 No Content on success;
 * the unwrap path treats the empty body as success. Order line items
 * snapshot price at purchase time, so deletion is non-retroactive.
 */
export async function deleteProductTier(productId: number, tierId: number): Promise<void> {
  try {
    await apiClient.delete(productTierItemPath(productId, tierId));
  } catch (error) {
    throwAsApiError(error);
  }
}

// ============================================================================
// Custom-size pricing rates (admin) — 4 endpoints (PL-STICKER-107)
//
// Product-scoped only today (see CustomSizePricingRateRepository::
// ALLOWED_PRICEABLE_TYPES on the platform). A custom (non-preset) size
// prices per piece as max(minPricePerUnit, ratePerSqIn × width × height),
// with the bracket selected by piece quantity.
// ============================================================================

function customSizeRateCollectionPath(productId: number): string {
  return `/admin/products/${encodeURIComponent(String(productId))}/custom-size-pricing-rates`;
}

function customSizeRateItemPath(productId: number, rateId: number): string {
  return `${customSizeRateCollectionPath(productId)}/${encodeURIComponent(String(rateId))}`;
}

interface SingleCustomSizeRateData {
  rate: CustomSizePricingRateDTO;
}

interface CustomSizeRateListData {
  rates: CustomSizePricingRateDTO[];
}

async function unwrapCustomSizeRateResponse(
  promise: Promise<{ status: number; data: ApiEnvelope<SingleCustomSizeRateData> }>,
): Promise<CustomSizePricingRateDTO> {
  try {
    const response = await promise;
    const data = unwrap(response.data, response.status);
    if (!data.rate) {
      throw new Error('Custom-size pricing rate envelope missing `data.rate`');
    }
    return data.rate;
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `GET /api/v1/admin/products/{productId}/custom-size-pricing-rates`
 *
 * Lists every rate bracket ordered by `minQty` ascending. Empty array
 * (NOT 404) means "no rates configured" — the pricing engine falls back to
 * the product's flat tier ladder for custom sizes in that case.
 */
export async function listCustomSizePricingRates(
  productId: number,
): Promise<CustomSizePricingRateDTO[]> {
  try {
    const response = await apiClient.get<ApiEnvelope<CustomSizeRateListData>>(
      customSizeRateCollectionPath(productId),
    );
    const data = unwrap(response.data, response.status);
    return data.rates ?? [];
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `POST /api/v1/admin/products/{productId}/custom-size-pricing-rates`
 *
 * Range-overlap with an existing bracket returns 409; validation failures
 * (non-positive numbers, `max_qty <= min_qty`) return 422.
 */
export async function createCustomSizePricingRate(
  productId: number,
  payload: CreateCustomSizePricingRatePayload,
): Promise<CustomSizePricingRateDTO> {
  return unwrapCustomSizeRateResponse(
    apiClient.post<ApiEnvelope<SingleCustomSizeRateData>>(
      customSizeRateCollectionPath(productId),
      payload,
    ),
  );
}

/**
 * `PUT /api/v1/admin/products/{productId}/custom-size-pricing-rates/{rateId}`
 *
 * Partial update — payload must carry at least one field. Range-overlap
 * with a sibling bracket (excluding this one) returns 409.
 */
export async function updateCustomSizePricingRate(
  productId: number,
  rateId: number,
  payload: UpdateCustomSizePricingRatePayload,
): Promise<CustomSizePricingRateDTO> {
  return unwrapCustomSizeRateResponse(
    apiClient.put<ApiEnvelope<SingleCustomSizeRateData>>(
      customSizeRateItemPath(productId, rateId),
      payload,
    ),
  );
}

/**
 * `DELETE /api/v1/admin/products/{productId}/custom-size-pricing-rates/{rateId}`
 *
 * Hard delete — returns 204 No Content on success.
 */
export async function deleteCustomSizePricingRate(
  productId: number,
  rateId: number,
): Promise<void> {
  try {
    await apiClient.delete(customSizeRateItemPath(productId, rateId));
  } catch (error) {
    throwAsApiError(error);
  }
}

// ============================================================================
// Material-scope (admin) — 5 endpoints
// ============================================================================

/**
 * `POST /api/v1/admin/products/{productId}/materials/{materialId}/pricing-tiers`
 *
 * Creates a new material-scope pricing tier. The `priceableType` on the
 * resulting DTO is `'ProductMaterial'`. Same overlap / validation rules
 * apply as the product-scope create.
 */
export async function createMaterialTier(
  productId: number,
  materialId: number,
  payload: CreatePricingTierPayload,
): Promise<PricingTierDTO> {
  return unwrapTierResponse(
    apiClient.post<ApiEnvelope<SingleTierData>>(
      materialTierCollectionPath(productId, materialId),
      payload,
    ),
  );
}

/**
 * `GET /api/v1/admin/products/{productId}/materials/{materialId}/pricing-tiers`
 *
 * Lists every material-scope tier for the given product↔material pivot,
 * ordered by `minQty` ascending. Empty array is a valid response.
 */
export async function listMaterialTiers(
  productId: number,
  materialId: number,
): Promise<PricingTierDTO[]> {
  return unwrapTiersResponse(
    apiClient.get<ApiEnvelope<TierListData>>(materialTierCollectionPath(productId, materialId)),
  );
}

/**
 * `GET /api/v1/admin/products/{productId}/materials/{materialId}/pricing-tiers/{tierId}`
 *
 * Reads a single material-scope tier.
 */
export async function getMaterialTier(
  productId: number,
  materialId: number,
  tierId: number,
): Promise<PricingTierDTO> {
  return unwrapTierResponse(
    apiClient.get<ApiEnvelope<SingleTierData>>(
      materialTierItemPath(productId, materialId, tierId),
    ),
  );
}

/**
 * `PUT /api/v1/admin/products/{productId}/materials/{materialId}/pricing-tiers/{tierId}`
 */
export async function updateMaterialTier(
  productId: number,
  materialId: number,
  tierId: number,
  payload: UpdatePricingTierPayload,
): Promise<PricingTierDTO> {
  return unwrapTierResponse(
    apiClient.put<ApiEnvelope<SingleTierData>>(
      materialTierItemPath(productId, materialId, tierId),
      payload,
    ),
  );
}

/**
 * `DELETE /api/v1/admin/products/{productId}/materials/{materialId}/pricing-tiers/{tierId}`
 *
 * Hard-deletes a material-scope tier. 204 No Content on success.
 */
export async function deleteMaterialTier(
  productId: number,
  materialId: number,
  tierId: number,
): Promise<void> {
  try {
    await apiClient.delete(materialTierItemPath(productId, materialId, tierId));
  } catch (error) {
    throwAsApiError(error);
  }
}

// ============================================================================
// Public — 1 endpoint
// ============================================================================

/**
 * `GET /api/v1/products/{productId}/pricing-tiers` — PUBLIC, unauthenticated.
 *
 * Mirrors `listProductTiers` exactly but on the public route stack. The
 * storefront's price calculator runs before login so anonymous shoppers
 * can preview volume breakpoints — published tier prices are not
 * sensitive data. Returns the same array shape as the admin endpoint.
 */
export async function listPublicProductTiers(productId: number): Promise<PricingTierDTO[]> {
  return unwrapTiersResponse(
    apiClient.get<ApiEnvelope<TierListData>>(publicProductTierCollectionPath(productId)),
  );
}

// ============================================================================
// Price preview — 1 endpoint (public)
// ============================================================================

/** Request body for POST /v1/products/{id}/price-preview. */
export interface PricePreviewPayload {
  readonly material_id?: number;
  /** Selected preset size (`size_quantity_matrix` products) — picks the per-size price ladder. */
  readonly predefined_size_id?: number;
  readonly sizing_mode: string;
  readonly quantity_sqft: number;
  readonly quantity?: number;
  readonly option_value_ids?: readonly number[];
  /**
   * Custom-size width/height, inches — sent only for a buyer-entered custom
   * size (no `predefined_size_id`). The server validates both against the
   * product type's custom-size bounds and 422s with the resolved numbers
   * when out of range (SF-STICKER-104).
   */
  readonly width_in?: number;
  readonly height_in?: number;
  /**
   * Free-form sizing payload. For `sizing_mode: 'panels'` this carries the
   * panel repeater as `{ panels: PanelPayload[] }` — the server prices the
   * list and returns the areas in `billableArea` (PL-WRAP-101/103).
   */
  readonly dimensions_json?: Record<string, unknown>;
}

/**
 * One row of the panel repeater, as the platform's `PanelDTO` reads it out of
 * `dimensions_json.panels`.
 *
 * `artworkFileId` is null while the row's file is still uploading — a preview
 * prices the sizes typed so far, and only add-to-cart requires the file.
 */
export interface PanelPayload {
  readonly lengthInches: number;
  readonly widthInches: number;
  readonly quantity: number;
  readonly artworkFileId: number | null;
  readonly sortOrder: number;
}

/** The computed price breakdown returned by the preview endpoint (`data.price`). */
export interface PricePreviewDTO {
  readonly unitPrice: number;
  readonly lineTotal: number;
  readonly breakdown: {
    readonly base: number;
    readonly material: number;
    readonly options: number;
    readonly addOns: number;
  };
  /** Present only for a panel-based selection; null on every other sizing mode. */
  readonly billableArea?: BillableAreaDTO | null;
}

interface PricePreviewData {
  price: PricePreviewDTO;
}

/**
 * `POST /api/v1/products/{productId}/price-preview` — PUBLIC.
 *
 * Computes a live price breakdown for a configurator selection (material +
 * sizing + option values). Returns the backend-computed breakdown — the
 * frontend never recomputes price.
 */
export async function previewProductPrice(
  productId: number,
  payload: PricePreviewPayload,
): Promise<PricePreviewDTO> {
  const id = encodeURIComponent(String(productId));
  try {
    const response = await apiClient.post<ApiEnvelope<PricePreviewData>>(
      `/products/${id}/price-preview`,
      payload,
    );
    return unwrap(response.data, response.status).price;
  } catch (error) {
    throwAsApiError(error);
  }
}

// ============================================================================
// Price breaks — 1 endpoint (public)
// ============================================================================

interface PriceBreaksData {
  priceBreaks: PriceBreakDTO[];
}

/**
 * `GET /api/v1/products/{productId}/price-breaks` — PUBLIC.
 *
 * Returns one row per quantity bracket in the product's tier ladder (the
 * `predefinedSizeId`-scoped ladder when given, else the product's own
 * ladder), each priced with the given material + option selections through
 * the same engine as `previewProductPrice`. The frontend never recomputes
 * a bracket's total — every `lineTotal` here is backend-computed.
 */
export async function listProductPriceBreaks(
  productId: number,
  params: PriceBreaksParams = {},
): Promise<PriceBreakDTO[]> {
  const query: Record<string, number | number[]> = {};
  if (params.materialId !== undefined) query.material_id = params.materialId;
  if (params.predefinedSizeId !== undefined) query.predefined_size_id = params.predefinedSizeId;
  if (params.optionValueIds && params.optionValueIds.length > 0) {
    query.option_value_ids = [...params.optionValueIds];
  }
  if (params.widthIn !== undefined) query.width_in = params.widthIn;
  if (params.heightIn !== undefined) query.height_in = params.heightIn;

  try {
    const response = await apiClient.get<ApiEnvelope<PriceBreaksData>>(
      `/products/${encodeURIComponent(String(productId))}/price-breaks`,
      { params: query },
    );
    return unwrap(response.data, response.status).priceBreaks ?? [];
  } catch (error) {
    throwAsApiError(error);
  }
}

// ============================================================================
// Predefined-size price matrix (admin) — size × quantity grid.
//
// The matrix belongs to a ProductTypeConfig, not a product: every product
// sharing that configurator type sells against these sizes and rates.
// ============================================================================

/**
 * `GET /api/v1/admin/product-type-configs/{id}/predefined-sizes`
 *
 * Returns every preset size for the config — including inactive ones, so an
 * admin can restore them — each with its own quantity ladder.
 */
export async function getPredefinedSizeMatrix(
  configId: number,
): Promise<PredefinedSizeMatrixRowDTO[]> {
  return unwrapMatrixResponse(
    apiClient.get<ApiEnvelope<PredefinedSizeMatrixData>>(predefinedSizeMatrixPath(configId)),
  );
}

/**
 * `PUT /api/v1/admin/product-type-configs/{id}/predefined-sizes`
 *
 * Full atomic replace of the matrix: sizes omitted from the payload are
 * deleted, and each size's ladder replaces whatever it had. One request for
 * the whole grid rather than one per cell.
 */
export async function savePredefinedSizeMatrix(
  configId: number,
  payload: SavePredefinedSizeMatrixPayload,
): Promise<PredefinedSizeMatrixRowDTO[]> {
  return unwrapMatrixResponse(
    apiClient.put<ApiEnvelope<PredefinedSizeMatrixData>>(
      predefinedSizeMatrixPath(configId),
      payload,
    ),
  );
}
